import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import { withStyles } from '@material-ui/core/styles';
import Autocomplete from "@material-ui/lab/Autocomplete";

import Cycle from '../../store/Cycle';
import { athenaGreen, statRed } from '../../globals';


const styles = theme => ({
  paper: {
    padding: theme.spacing(2),
  },
  month: {
    flexBasis: 120,
  },
  changeValue: {
    flexBasis: 100,
  },
  growthUnit: {
    '& p': {
      fontSize: 12,
      marginTop: 21,
    },
  },
  labelTopRed: {
    color: theme.palette.secondary.main,
    fontWeight: 800,
    fontSize: 14,
    minWidth: 77,
  },
  labelTopGreen: {
    color: athenaGreen,
    fontWeight: 800,
    fontSize: 14,
  },
  labelBottom: {
    color: '#333',
    fontSize: 14,
  },
});


@withStyles(styles)
@observer
class VersionChangesInput extends React.Component {
  static propTypes = {
    type: PropTypes.string.isRequired,
    label: PropTypes.string,
    filters: PropTypes.shape().isRequired,
    months: PropTypes.arrayOf(PropTypes.number),
    changeUnit: PropTypes.string,
    value: PropTypes.any.isRequired,
    onChange: PropTypes.func.isRequired,
    helpText: PropTypes.any,
    disabled: PropTypes.bool,
  };

  static defaultProps = {
    label: '',
    changeUnit: '%',
    helpText: null,
    disabled: false,
  };

  onAddChange = () => {
    const { filters, changeUnit, months, value, onChange } = this.props;
    const newRow = {
      filters: Object.fromEntries(Object.keys(filters).map(name => [name, []])),
      months: months,
      change_value: changeUnit === 'binary' ? true : 0,
    };
    if (months === null) {
      newRow.monthsStart = null;
      newRow.monthsEnd = null;
    } else {
      newRow.monthsStart = months[0];
      newRow.monthsEnd = months[months.length-1];
    }
    onChange([...value, newRow]);
  };

  copyValue = () => JSON.parse(JSON.stringify(this.props.value));

  onFilterChange = (type, index) => {
    return (event, value) => {
      const newValue = this.copyValue();
      newValue[index].filters[type] = value;
      this.props.onChange(newValue);
    };
  };

  updateMonths = (newValue, index) => {
    const { months, onChange } = this.props;
    const changeValues = newValue[index];
    changeValues.months = months.filter(
      month => (month >= changeValues.monthsStart) && (month <= changeValues.monthsEnd)
    );
    onChange(newValue);
  };

  onMonthsStartChange = index => {
    return event => {
      const newValue = this.copyValue();
      newValue[index].monthsStart = event.target.value;
      this.updateMonths(newValue, index);
    };
  };

  onMonthsEndChange = index => {
    return event => {
      const newValue = this.copyValue();
      newValue[index].monthsEnd = event.target.value;
      this.updateMonths(newValue, index);
    };
  };

  onGrowthChange = index => {
    return event => {
      const { changeUnit, onChange } = this.props;
      const newValue = this.copyValue();
      newValue[index].change_value = (
        changeUnit === 'binary' ? event.target.checked : event.target.value
      );
      onChange(newValue);
    };
  };

  onClose = index => {
    return () => {
      const newValue = this.copyValue();
      newValue.splice(index, 1);
      this.props.onChange(newValue);
    };
  };

  getMonthsInputs = index => {
    const changeData = this.props.value[index];
    if (changeData.months === null) {
      return null;
    }
    const monthsStart = changeData.months[0];
    const monthsEnd = changeData.months[changeData.months.length-1];
    const { months, classes, disabled } = this.props;
    return (
      <>
        <Grid item className={classes.month}>
          <TextField
            select label="Months start" value={monthsStart}
            onChange={this.onMonthsStartChange(index)} fullWidth
            variant="outlined" disabled={disabled}
          >
            {months.filter(month => month <= monthsEnd).map(month =>
              <MenuItem key={month} value={month}>
                {Cycle.fromPythonTimestamp(month).toString()}
              </MenuItem>
            )}
          </TextField>
        </Grid>
        <Grid item className={classes.month}>
          <TextField
            select label="Months end" value={monthsEnd}
            onChange={this.onMonthsEndChange(index)} fullWidth
            variant="outlined" disabled={disabled}
          >
            {months.filter(month => month >= monthsStart).map(month =>
              <MenuItem key={month} value={month}>
                {Cycle.fromPythonTimestamp(month).toString()}
              </MenuItem>
            )}
          </TextField>
        </Grid>
      </>
    );
  };

  getChangeValueInput = index => {
    const { changeUnit, value, disabled, classes } = this.props;
    const changeValue = value[index].change_value;
    if(changeUnit !== 'binary') {
      return (
        <Grid item className={classes.changeValue}>
          <TextField
            label="Growth"
            type="number"
            value={changeValue}
            onChange={this.onGrowthChange(index)}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" className={classes.growthUnit}>
                  {changeUnit}
                </InputAdornment>
              )
            }}
            disabled={disabled}
            fullWidth
          />
        </Grid>
      );
    }
  };

  getIncludeInScenario = index => {
    const { changeUnit, value, disabled, classes } = this.props;
    const changeValue = value[index].change_value;
    const GreenCheckbox = withStyles({
      root: {
        color: statRed,
        '&$checked': {
          color: athenaGreen,
        },
      },
      checked: {},
    })(Checkbox);
    if(changeUnit === 'binary'){
      return (
        <Grid item className={classes.changeValue}>
          <FormControlLabel
            control={
              <GreenCheckbox
                checked={changeValue}
                onChange={this.onGrowthChange(index)}
              />
            }
            label={
              <Grid container wrap="nowrap">
                <Grid item>
                  <Typography variant="body1" className={changeValue ? classes.labelTopGreen : classes.labelTopRed}>{changeValue ? 'Include' : 'Exclude'}</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" className={classes.labelBottom}>{changeValue ? 'in' : 'from' } scenario</Typography>
                </Grid>
              </Grid>
            }
            disabled={disabled}
          />
        </Grid>
      );
    }
  };

  render() {
    const { type, label, filters, value, helpText, disabled, classes } = this.props;
    return (
      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>“{label || type}” changes</Typography>
        <Grid container spacing={2} direction="column" wrap="nowrap">
          {value.map((changeData, index) => {
            return (
              <Grid item key={index}>
                <Grid container spacing={1} alignItems="center">
                  {this.getIncludeInScenario(index)}
                  {Object.entries(filters).map(([name, values]) =>
                    <Grid item xs key={name}>
                      <Autocomplete
                        multiple
                        options={[...values]}
                        value={changeData.filters[name]}
                        onChange={this.onFilterChange(name, index)}
                        renderInput={params => <TextField {...params} label={name} variant="outlined" />}
                        getOptionLabel={option => `${option}`}
                        renderOption={(option, { selected }) =>
                          <React.Fragment>
                            <Checkbox checked={selected} />
                            {option}
                          </React.Fragment>
                        }
                        fullWidth
                        disableCloseOnSelect
                        disableClearable
                        disabled={disabled}
                      />
                    </Grid>
                  )}
                  {this.getMonthsInputs(index)}
                  {this.getChangeValueInput(index)}
                  <Grid item>
                    <IconButton onClick={this.onClose(index)} disabled={disabled}>
                      <CloseIcon color="secondary" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
          <Grid item>
            <Grid container alignItems="center" spacing={2} wrap="nowrap">
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={this.onAddChange}
                  startIcon={<AddIcon />}
                  disabled={disabled}
                >
                  New change
                </Button>
              </Grid>
              {
                helpText === null
                  ? null
                  : (
                    <Grid item xs>
                      <Typography>{helpText}</Typography>
                    </Grid>
                  )
              }
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    );
  }
}


export default VersionChangesInput;
